import { create } from "zustand";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "rensure-theme";

// Reads persisted theme (defaults to dark to match existing UI)
const readInitial = (): ThemeMode => {
  if (typeof window === "undefined") return "dark";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* no-op */
  }
  return "dark";
};

// Applies `dark` class on <html> when using dark palette
const applyDom = (mode: ThemeMode) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
};

const persist = (mode: ThemeMode) => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* no-op */
  }
};

interface ThemeState {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

export const useTheme = create<ThemeState>((set) => ({
  theme: readInitial(),
  setTheme: (mode) => {
    applyDom(mode);
    persist(mode);
    set({ theme: mode });
  },
  toggleTheme: () =>
    set((prev) => {
      const next = prev.theme === "dark" ? "light" : "dark";
      applyDom(next);
      persist(next);
      return { theme: next };
    }),
}));

applyDom(readInitial());
